<template>
  <Authenticated>
    <div class="reports">
      <section class="main-content">
        <section class="totals-section">
          <div class="box-shadow">
            <ReportsComponent :dataType="dataType" :site="site" :day="day" :otherDay="otherDay" :period="'today'" />
          </div>
          <div class="box-shadow">
            <ReportsComponent :dataType="dataType" :site="site" :day="week" :otherDay="otherWeek" :period="'week'" />
          </div>
        </section>
        <section class="submenu">
          <div class="column-container">
            <div class="radio-btn inner-section">
              <div>
                <input type="radio" name="salesType" value="sales" v-model="dataType" class="radio-blue" />
                <label for="sales">Sales</label>
              </div>
              <div>
                <input type="radio" name="salesType" value="count" v-model="dataType" class="radio-blue" />
                <label for="count">Count</label>
              </div>
            </div>
            <div class="site-select inner-section">
              <div v-if="sites.length > 1">
                <!-- get the selected from the url -->
                <Carousel :items="getSiteOptions()" title="Select Site" @select-item="selectSite" />
              </div>
            </div>
          </div>
          <div class="column-container">
            <div class="filter-nav inner-section">
              <p>Preset Range / Custom Range</p>
              <ToggleSwitch :model-name="'range-picker'" :model-value="range" @update:modelValue="updateView" on-label="Custom" off-label="Preset" />
            </div>
            <div class="periodSelect inner-section">
              <Carousel v-if="range" :items="rangeTimeFrame" :style="'arrow'" title="Select Range Period" @select-item="selectRangePeriod" />
              <Carousel v-else :items="timeframe" :style="'arrow'" title="Select Period" @select-item="selectPeriod" />
            </div>
          </div>
        </section>
      </section>
      <section class="charts-section">
        <!-- when range is selected -->
        <div class="chart">
          <div class="day-btn" v-if="range">
            <div class="date-range-picker">
              <div class="labels">
                <label for="start-date">Start Date:</label>
                <input type="date" v-if="rangePeriod === 'dayRange'" class="day-picker" id="start-date" v-model="dateRange.start" />
                <input type="week" v-if="rangePeriod === 'weekRange'" class="week-picker" id="start-date" v-model="weekRange.start" />
                <input type="month" v-if="rangePeriod === 'monthRange'" class="month-picker" id="start-date" v-model="monthRange.start" />
              </div>
              <div class="labels">
                <label for="end-date">End Date:</label>
                <input type="date" v-if="rangePeriod === 'dayRange'" class="day-picker" id="end-date" v-model="dateRange.end" />
                <input type="week" v-if="rangePeriod === 'weekRange'" class="week-picker" id="end-date" v-model="weekRange.end" />
                <input type="month" v-if="rangePeriod === 'monthRange'" class="month-picker" id="end-date" v-model="monthRange.end" />
              </div>
              <button class="action-colour" @click.stop.prevent="updateRange">Search Range</button>
            </div>
            <LineChart
              v-if="dateChosen"
              ref="lineChart"
              :site="site"
              :dataType="dataType"
              :day="currentRange"
              :otherDay="rangeComparator"
              :period="rangePeriod"
              :chartType="chartType"
              :key="`${currentRange}-${rangeComparator}`"
              @handle-export-data="handleExportData" />
          </div>

          <!-- regular date picker -->
          <div class="day-btn" v-else>
            <div class="arrows">
              <button class="action-colour" @click.stop.prevent="changeDay(-1, period)">
                <font-awesome-icon icon="chevron-left" />
              </button>
              <div class="date-picker">
                <input class="day-picker" v-if="period === 'day'" type="date" v-model="pickDate" />
                <input class="week-picker" v-if="period === 'week'" type="week" v-model="pickWeek" />
                <input class="month-picker" v-if="period === 'month'" type="month" v-model="pickMonth" />
                <input class="month-picker" v-if="period === 'year'" type="number" v-model="pickYear" min="1970" max="3000" />
              </div>
              <button class="action-colour" @click.stop.prevent="changeDay(1, period)">
                <font-awesome-icon icon="chevron-right" />
              </button>
            </div>
            <LineChart
              ref="lineChart"
              :site="site"
              :dataType="dataType"
              :day="current"
              :otherDay="comparator"
              :period="period"
              :chartType="chartType"
              :key="`${current}-${comparator}`"
              @handle-export-data="handleExportData" />
          </div>
        </div>
        <div class="chart">
          <BarChart v-if="!range" ref="barChart" :site="site" :dataType="dataType" :day="stackedCurrent" :period="period" @handle-export-data="handleExportData" />
          <BarChart v-if="range" ref="barChart" :site="site" :dataType="dataType" :day="stackedRange" :period="rangePeriod" @handle-export-data="handleExportData" />
        </div>
        <div class="exporting">
          <p class="export" @click.stop.prevent="exportData(this.exportTotalsData, 'totals', 'totals')">
            Export totals data as CSV
            <font-awesome-icon icon="fa-file-export" />
          </p>
          <p class="export" @click.stop.prevent="exportData(this.exportPaymentsData, 'payments', 'payments')">
            Export payments data as CSV
            <font-awesome-icon icon="fa-file-export" />
          </p>
          <p class="export" @click.stop.prevent="exportData(this.exportStackedData, 'stacked', 'stacked')">
            Export stacked data as CSV
            <font-awesome-icon icon="fa-file-export" />
          </p>
          <p class="export" @click.stop.prevent="exportData(this.exportStackedProductsData, 'stackedProducts', 'stackedProducts')">
            Export stacked products data as CSV
            <font-awesome-icon icon="fa-file-export" />
          </p>
        </div>
      </section>
    </div>
  </Authenticated>
</template>

<script>
import Authenticated from "@/components/_layouts/Authenticated";
import { mapGetters } from "vuex";
import LineChart from "@/components/Reports/LineChart.vue";
import { dayRange, reports } from "@monolith/helpers";
import ReportsComponent from "@/components/Reports/ReportsComponent.vue";
import BarChart from "@/components/Reports/BarChart.vue";
import { Carousel, ToggleSwitch } from "@monolith/components";
import { Date } from "core-js";

export default {
  name: "reports",
  components: { Authenticated, LineChart, ReportsComponent, BarChart, Carousel, ToggleSwitch },
  data() {
    return {
      chartType: "bar",
      period: "day",
      reportData: 0,
      renderComponent: true,
      productCount: {},
      site: this.$route.params.site || "all",
      dataType: this.$route.params.dataType || "sales",
      currentDate: new Date(),
      currentDay: [],
      today: null,
      current: dayRange.getToday(),
      comparator: dayRange.getLastWeekDay(),
      day: dayRange.getToday(),
      otherDay: dayRange.getLastWeekDay(),
      week: dayRange.getThisWeek(),
      otherWeek: dayRange.getLastWeek(),
      month: dayRange.getThisMonth(),
      gettingToday: dayRange.getToday(),
      pickDate: null,
      pickWeek: null,
      pickMonth: null,
      otherMonth: dayRange.getLastYearMonth(),
      company: localStorage.getItem("company"),
      timeframe: [
        { name: "day", value: "day", selected: true },
        { name: "week", value: "week" },
        { name: "month", value: "month" },
        { name: "year", value: "year" },
      ],
      rangeTimeFrame: [
        { name: "dayRange", value: "dayRange", selected: true },
        { name: "weekRange", value: "weekRange" },
        { name: "monthRange", value: "monthRange" },
      ],
      range: false,
      rangePeriod: "",
      dateRange: {
        start: null,
        end: null,
      },
      weekRange: {
        start: null,
        end: null,
      },
      monthRange: {
        start: null,
        end: null,
      },
      stackedCurrent: dayRange.getToday(), //for the stacked graph
      currentRange: dayRange.getToday(), // equivalent to current, used for range picker
      rangeComparator: [], // get the dates but last year same range
      dateChosen: false, // to stop chart loading before dates chosen
      stackedRange: [], // need to get the dates for the range
      exportTotalsData: null,
      exportPaymentsData: null,
      exportStackedData: null,
      exportStackedProductsData: null,
    };
  },

  methods: {
    exportData(data, type, file, dataType) {
      // individual data, type such as totals and file name
      this.$refs.lineChart.exportData(data, type, file);
      this.$refs.barChart.exportData(data, type, file);
      let csvRows = [];

      if (type == "totals") {
        let titleKeys = [];
        let titleLabels = [];
        if (this.period == "day") {
          titleKeys = ["key", "value"];
          titleLabels = ["Hour", dataType == "sales" ? "Sales" : "Count"];
        } else if (this.period == "month" || this.period == "dayRange" || this.period == "week") {
          titleKeys = ["key", "value"];
          titleLabels = ["Date", dataType == "sales" ? "Sales" : "Count"];
        } else if (this.period == "weekRange") {
          titleKeys = ["key", "value"];
          titleLabels = ["Week", dataType == "sales" ? "Sales" : "Count"];
        } else if (this.period == "year" || this.period == "monthRange") {
          titleKeys = ["key", "value"];
          titleLabels = ["Month", dataType == "sales" ? "Sales" : "Count"];
        }

        csvRows = [titleLabels.join(",")];
        for (let row of data.value) {
          let values = titleKeys.map((key) => {
            let val = row[key];
            return typeof val === "string" && val.includes(",") ? `"${val}"` : val;
          });
          csvRows.push(values.join(","));
        }
      } else if (type == "payments") {
        // Add payment breakdown section
        csvRows.push("Payment Breakdown");
        csvRows.push("Type,Amount");
        for (let payment of data.value) {
          csvRows.push(`${payment.key},$${payment.value.toFixed(2)}`);
        }
      } else if (type == "stacked") {
        let titleLabels = ["Category", "Value"];
        let titleKeys = ["key", "value"];

        csvRows = [titleLabels.join(",")];
        for (let row of data.value) {
          let values = titleKeys.map((key) => {
            let val = row[key];
            return typeof val === "string" && val.includes(",") ? `"${val}"` : val;
          });
          csvRows.push(values.join(","));
        }
      } else if (type == "stackedProducts") {
        let titleLabels = ["Product", "Value"];
        let titleKeys = ["key", "value"];

        csvRows = [titleLabels.join(",")];
        for (let row of data.value) {
          let values = titleKeys.map((key) => {
            let val = row[key];
            return typeof val === "string" && val.includes(",") ? `"${val}"` : val;
          });
          csvRows.push(values.join(","));
        }
      }

      let csvContent = csvRows.join("\n");

      let blob = new Blob([csvContent], { type: "text/csv" });
      let url = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      link.href = url;
      link.download = file + ".csv";
      link.click();

      // empty the array for next call
      this.exportedData = [];
    },
    handleExportData(csvContent) {
      if (csvContent.key == "totals") {
        this.exportTotalsData = csvContent;
      } else if (csvContent.key == "payments") {
        this.exportPaymentsData = csvContent;
      } else if (csvContent.key == "stacked") {
        this.exportStackedData = csvContent;
      } else if (csvContent.key == "stackedProducts") {
        this.exportStackedProductsData = csvContent;
      }
    },

    updateView() {
      // used to change from the normal graph view to the range picker
      // changes the html and what is passed to the graphs
      this.range = !this.range;
    },

    updateDateRange() {
      // format to match api
      let newStart = dayRange.formatDate(new Date(this.dateRange.start));
      let additionalDay = new Date(this.dateRange.end);
      additionalDay.setDate(additionalDay.getDate() + 1); // include end date
      let newEnd = dayRange.formatDate(additionalDay);

      // the current range is the first date to the last date
      this.currentRange = [newStart, newEnd];

      // the comparator is the previous range
      this.rangeComparator = dayRange.compareLastYear(new Date(this.dateRange.start), new Date(additionalDay));

      // get all the new data
      reports.getData(newStart, newEnd, this.dataType, this.company, this.site);
      reports.getTotals(newStart, newEnd, this.dataType, this.company, this.site);

      // for bar chart
      this.stackedRange = [newStart, newEnd];
    },

    updateWeekRange() {
      // format to match api
      let [startYear, startWeek] = this.weekRange.start.split("-W");
      let [endYear, endWeek] = this.weekRange.end.split("-W");

      // the current range is the first date to the last date - for the two graphs
      this.currentRange = [this.weekRange.start, this.weekRange.end];
      this.stackedRange = [this.weekRange.start, this.weekRange.end];

      // the comparator is the previous range
      this.rangeComparator = [`${parseInt(startYear) - 1}-W${startWeek}`, `${parseInt(endYear) - 1}-W${endWeek}`];

      // get all the data
      reports.getData(this.weekRange.start, this.weekRange.end, this.dataType, this.company, this.site);
      reports.getTotals(this.weekRange.start, this.weekRange.end, this.dataType, this.company, this.site);
    },

    updateMonthRange() {
      let [startYear, startMonth] = this.monthRange.start.split("-");
      let [endYear, endMonth] = this.monthRange.end.split("-");

      this.currentRange = [this.monthRange.start, this.monthRange.end];
      this.rangeComparator = [`${parseInt(startYear) - 1}-${startMonth}`, `${parseInt(endYear) - 1}-${endMonth}`];
      this.stackedRange = [this.monthRange.start, this.monthRange.end];

      reports.getData(this.monthRange.start, this.monthRange.end, this.dataType, this.company, this.site);
      reports.getTotals(this.monthRange.start, this.monthRange.end, this.dataType, this.company, this.site);
    },

    updateRange() {
      this.dateChosen = true; // to load in the graph after dates chosen
      if (this.rangePeriod === "dayRange") {
        this.updateDateRange();
      } else if (this.rangePeriod === "weekRange") {
        this.updateWeekRange();
      } else if (this.rangePeriod === "monthRange") {
        this.updateMonthRange();
      }
    },
    selectSite(site) {
      //this.site = site;
      if (this.site != site) {
        this.site = site;
        this.$router.push({ name: "reports", params: { site: this.site, dataType: this.dataType } });
      }
    },
    selectPeriod(selectedPeriod) {
      this.period = selectedPeriod;
    },
    selectRangePeriod(selectedPeriod) {
      this.dateChosen = false;
      this.rangePeriod = selectedPeriod;
    },
    getSiteOptions() {
      const s = [{ site: "all" }];
      const sites = s.concat(this.sites);

      return sites.map((site) => {
        return {
          name: site.site,
          value: site.site,
          selected: site.site === this.site,
        };
      });
    },

    // the left and right chevrons
    changeDay(direction, period) {
      if (period === "day") {
        this.currentDate = dayRange.editDay(direction, this.currentDate); // make today yesterday
        if (direction === -1) {
          this.comparator = dayRange.editWeek(direction, this.currentDate); // get last week of yesterday
        } else {
          this.comparator = dayRange.editWeek(-1, this.currentDate); // get next week of today
        }
        this.current = dayRange.getDayRange(this.currentDate); //get the day range of yesterday
        this.stackedCurrent = this.current;
        this.comparator = dayRange.getDayRange(this.comparator); //get the day range of last week yesterday
        this.pickDate = this.currentDate.toLocaleString().slice(0, 10).split("/").reverse().join("-");
      } else if (period === "week") {
        this.currentDate = dayRange.editWeek(direction, this.currentDate); // change the week
        if (direction === -1) {
          this.comparator = dayRange.editWeek(direction, this.currentDate); // use last week to get the week before
        } else {
          this.comparator = dayRange.editWeek(-1, this.currentDate); // use next week to get the week after
        }
        this.current = dayRange.getWeekRange(this.currentDate); // get the week range of the other week
        this.comparator = dayRange.getWeekRange(this.comparator); // get the week range of the week before
        this.pickWeek = reports.getISOWeekString(this.currentDate);
        this.stackedCurrent = [this.pickWeek, 0];
      } else if (period === "month") {
        this.currentDate = dayRange.editMonth(direction, this.currentDate); // change the month
        if (direction === -1) {
          this.comparator = dayRange.editMonth(direction, this.currentDate); // use last month to get the month before
        } else {
          this.comparator = dayRange.editMonth(-1, this.currentDate); // use next month to get the month after
        }
        this.current = dayRange.getMonthRange(this.currentDate); // get the month range of the other month
        this.stackedCurrent = this.current;
        this.comparator = dayRange.getMonthRange(this.comparator); // get the month range of the month before
        this.pickMonth = this.currentDate.toISOString().slice(0, 7);
      } else if (period == "year") {
        // Create a new date using the original month/day but with the new year
        let oldDate = this.currentDate instanceof Date ? this.currentDate : new Date(this.currentDate);
        let newYear = dayRange.editYear(direction, oldDate);
        this.currentDate = new Date(oldDate.setFullYear(newYear));

        if (direction === -1) {
          this.comparator = dayRange.editYear(direction, this.currentDate); // use previous year to get year before
        } else {
          this.comparator = dayRange.editYear(-1, this.currentDate); // use next year to get year after
        }
        this.current = dayRange.getYearRange(this.currentDate.getFullYear()); // get the year range for the current year
        this.stackedCurrent = [this.currentDate, 0];
        this.comparator = dayRange.getYearRange(this.comparator); // get the year range of the comparator year
        this.pickYear = this.currentDate.getFullYear();
      }

      reports.getData(this.current[0], this.current[1], this.dataType, this.company, this.site);
      reports.getTotals(this.current[0], this.current[1], this.dataType, this.company, this.site);
    },

    setReportSite: function (event) {
      this.$store.dispatch("setReportSite", event.target.value);
      this.report("week_total");
    },
  },
  mounted() {
    this.currentDate = new Date();
    // populate the date pickers
    this.pickDate = new Date().toLocaleString().substring(0, 10).split("/").reverse().join("-");
    this.pickWeek = reports.getISOWeekString(new Date());
    this.pickMonth = new Date().toISOString().slice(0, 7);
    this.pickYear = new Date().getUTCFullYear();
    this.current = dayRange.getToday();
  },
  watch: {
    period: function () {
      this.currentDate = new Date();
      if (this.period == "day") {
        this.current = this.day;
        let currentStart = reports.fixDateRangeUTC(this.day[0]);
        let currentEnd = reports.fixDateRangeUTC(this.day[1]);
        this.stackedCurrent = [reports.formatFromDate(currentStart), reports.formatFromDate(currentEnd)]; // get the start and end of the day
        this.comparator = this.otherDay;
      } else if (this.period == "week") {
        this.stackedCurrent = [this.pickWeek, 0]; // send as an array for formatting, dates are calculated in child
        this.current = this.week;
        this.comparator = this.otherWeek;
        // both current and comparator bring back sunday to sunday in format 2025-11-16T11
        // T is hours in 24 hour time
      } else if (this.period == "month") {
        this.stackedCurrent = this.month; // may need to be changed when implemented
        this.current = this.month;
        this.comparator = this.otherMonth;
      } else if (this.period == "year") {
        let yearDates = dayRange.getYearRange(this.pickYear); // returns [2024-12-21T11, 2025-12-31T11]
        this.stackedCurrent = [this.pickYear, 0];
        this.current = yearDates;
        let lastYear = dayRange.editYear(-1, new Date(this.pickYear, "01", "01"));
        this.comparator = dayRange.getYearRange(lastYear);
      }
    },
    site: function () {
      this.$router.replace({ path: "/reports/" + this.site + "/" + this.dataType });
    },
    dataType: function () {
      this.$router.replace({ path: "/reports/" + this.site + "/" + this.dataType });
    },
    currentDay: function () {
      reports.getData(this.current[0], this.current[1], this.dataType, this.company, this.site);
      reports.getTotals(this.current[0], this.current[1], this.dataType, this.company, this.site);
    },
    pickDate: async function (newDate) {
      let date = new Date(newDate);
      this.currentDate = date;
      this.current = dayRange.getDayRange(date);
      this.stackedCurrent = this.current;
      reports.getData(this.current[0], this.current[1], this.dataType, this.company, this.site);
      reports.getTotals(this.current[0], this.current[1], this.dataType, this.company, this.site);
    },
    pickWeek: function (newWeek) {
      if (this.period == "week") {
        const [year, week] = newWeek.split("-W");
        this.currentDate = new Date(year, 0, 1 + (week - 1) * 7);
        this.current = dayRange.getWeekRange(this.currentDate);
        this.stackedCurrent = [newWeek, 0];
        reports.getData(this.current[0], this.current[1], this.dataType, this.company, this.site);
        reports.getTotals(this.current[0], this.current[1], this.dataType, this.company, this.site);
      }
      // current Date is the wednesday 12th november??
      // current is sunday through sunday 2025-11-09T11
      // stacked with {2025-W46, 0}
    },
    pickMonth: function (newMonth) {
      if (this.period == "month") {
        let date = new Date(newMonth);
        this.currentDate = date;
        this.current = dayRange.getMonthRange(date);
        this.stackedCurrent = this.current;
        reports.getData(this.current[0], this.current[1], this.dataType, this.company, this.site);
        reports.getTotals(this.current[0], this.current[1], this.dataType, this.company, this.site);
      }
    },
    pickYear: function (newYear) {
      if (this.period == "year") {
        this.current = dayRange.getYearRange(newYear);
        this.currentDate = this.current[0];
        reports.getData(this.current[0], this.current[1], this.dataType, this.company, this.site);
        reports.getTotals(this.current[0], this.current[1], this.dataType, this.company, this.site);

        this.stackedCurrent = [newYear, 0];
        let lastYear = dayRange.editYear(-1, new Date(newYear, "01", "01"));
        this.comparator = dayRange.getYearRange(lastYear);
      }
    },
  },
  computed: {
    ...mapGetters({
      sites: "siteMap",
      reportSite: "reportSite",
    }),
    getSite() {
      return this.$route.params.site;
    },
  },
};
</script>

<style scoped lang="scss">
@use "@monolith/styles";

// Shared card token — same background, shadow, radius and padding used everywhere
$card-bg: var(--tertiary-colour);
$card-radius: 10px;
$card-padding: 1.25rem;
$card-shadow: var(--box-shadow);

.reports {
  display: flex;
  flex-direction: column;
  background-color: var(--primary-colour);
  margin: 1.5rem;
  padding: 1rem 0;
  gap: 1rem;
  border-radius: $card-radius;
  box-shadow: $card-shadow;

  // ── .box-shadow: visual card wrapper only — stays block so ReportsComponent
  //    inside it sizes naturally to its own content height.
  .box-shadow {
    background: $card-bg;
    box-shadow: $card-shadow;
    border-radius: $card-radius;
    padding: $card-padding;
  }

  // ── .inner-section: submenu interactive cards — flex so radio/carousel/toggle
  //    can lay out their children properly.
  .inner-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: $card-bg;
    box-shadow: $card-shadow;
    border-radius: $card-radius;
    padding: $card-padding;

    // Kill browser-default paragraph margin (filter-nav label)
    > p {
      margin: 0;
    }
  }

  /* ========== MAIN CONTENT ========== */
  .main-content {
    display: grid;
    grid-template-columns: repeat(3, 1fr); // All three columns equal width
    grid-template-rows: repeat(2, 1fr);    // Equal rows — needs a height anchor below to work
    min-height: 22rem;                     // Gives 1fr rows a free-space pool; each row ≈ (22rem − gap) / 2
    gap: 1rem;
    margin: 0 1rem;

    // Unwrap all intermediate containers — all 6 cards become direct grid children
    .totals-section,
    .submenu,
    .column-container {
      display: contents;
    }

    // ── Column 1: Report cards ────────────────────────────────────────────────
    .totals-section .box-shadow:nth-child(1) { grid-column: 1; grid-row: 1; }
    .totals-section .box-shadow:nth-child(2) { grid-column: 1; grid-row: 2; }

    // ── Column 2: Sales type + Site select ───────────────────────────────────
    .radio-btn   { grid-column: 2; grid-row: 1; }
    .site-select { grid-column: 2; grid-row: 2; }

    // ── Column 3: Range toggle + Period select ────────────────────────────────
    .filter-nav   { grid-column: 3; grid-row: 1; }
    .periodSelect { grid-column: 3; grid-row: 2; }

    // Radio button internals
    .radio-btn {
      gap: 0.75rem;

      div {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 1rem;
      }

      .radio-blue {
        appearance: none;
        margin-left: 1rem;
        border-radius: 50%;
        transition: all 500ms ease-in-out;
        height: 1rem;
        width: 1rem;
        border: 2px solid var(--action-colour);

        &:checked {
          background-color: var(--action-colour);
          border: 5px solid var(--action-colour);
        }
      }
    }
  }

  /* ========== CHARTS SECTION ========== */
  .charts-section {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 0 1rem;

    .chart {
      display: flex;
      flex-direction: row;
      background: $card-bg;
      padding: $card-padding;
      border-radius: $card-radius;
      box-shadow: $card-shadow;

      .day-btn {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;

        .arrows {
          display: flex;
          align-items: center;

          .date-picker {
            display: flex;
            align-items: center;
            margin: 0.5rem;
          }
        }

        .date-range-picker {
          display: flex;
          align-items: center;
          gap: 1rem;

          .labels {
            display: flex;
            flex-direction: column;
            align-items: center;
          }
        }

        // Shared input styles for all date pickers
        .day-picker,
        .week-picker,
        .month-picker {
          border-radius: 5px;
          padding: 0.5rem 1rem;
          margin: 0.5rem;
          box-shadow: $card-shadow;
          background-color: $card-bg;
          border: none;
          color: var(--text-colour);
        }
      }
    }

    .exporting {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 1rem;

      .export {
        padding: 0.75rem 1rem;
        border: 0.5px solid var(--action-colour);
        border-radius: 0.25rem;
        color: var(--action-colour);
        cursor: pointer;
      }
    }
  }
}

/* ========== PORTRAIT ========== */
@media only screen and (orientation: portrait) {
  .reports {
    margin: 0.5rem;
    padding: 0.75rem 0;

    .main-content {
      // Switch from grid back to a simple flex column so each section stacks
      display: flex;
      flex-direction: column;
      min-height: unset;
      margin: 0 0.5rem;

      // Restore the wrappers that desktop hides with display: contents
      .totals-section,
      .submenu,
      .column-container {
        display: flex;
      }

      // Report cards sit side by side in a row
      .totals-section {
        flex-direction: row;
        gap: 1rem;

        .box-shadow {
          flex: 1;
          min-width: 0;      // Allows the card to shrink below its content's natural width
          overflow: hidden;  // Clips any content that still overflows after ellipsis kicks in
          padding: 0.75rem;
        }
      }

      // Both column-containers stack in a row, each a column of two cards
      .submenu {
        flex-direction: row;
        gap: 1rem;

        .column-container {
          flex: 1;
          flex-direction: column;
          gap: 1rem;

          .inner-section {
            flex: 1;
            padding: 0.75rem;
          }
        }
      }
    }

    .charts-section {
      margin: 0 0.5rem;

      .chart .day-btn {
        .arrows {
          max-width: 100%;

          button {
            margin: 0;
            padding: 0.5rem;
          }

          .date-picker { margin: 0; }
        }
      }
    }
  }
}
</style>
